import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '~/app/auth/context/AuthContext';
import { usePermission } from '~/app/auth/hooks/usePermission';
import { DEFAULT_LANDING_PATH, getRoleRedirectPath } from '~/app/auth/utils';
import type { PermissionKey } from '@evimo/types/enums/permission-key';
import { SystemRoleName } from '@evimo/types/enums/system-role-name';
import { Loader } from '~/app/components/common/Loader/Loader';

interface AuthGuardProps {
  children: React.ReactNode;
  requiredRole?: SystemRoleName;
  requiredPermissions?: PermissionKey[];
}

export function AuthGuard({
  children,
  requiredRole,
  requiredPermissions,
}: AuthGuardProps) {
  const { isAuthenticated, user, isLoading } = useAuth();
  const { hasAllPermissions } = usePermission();
  const navigate = useNavigate();

  const lacksRequiredRole = Boolean(
    requiredRole && user?.roleName !== requiredRole,
  );
  const lacksRequiredPermissions = Boolean(
    requiredPermissions?.length && !hasAllPermissions(requiredPermissions),
  );
  const isAccessDenied = lacksRequiredRole || lacksRequiredPermissions;

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }

    if (lacksRequiredRole) {
      navigate(getRoleRedirectPath(user?.roleName), { replace: true });
      return;
    }

    if (lacksRequiredPermissions) {
      navigate(DEFAULT_LANDING_PATH, { replace: true });
    }
  }, [
    isLoading,
    isAuthenticated,
    user?.roleName,
    lacksRequiredRole,
    lacksRequiredPermissions,
    navigate,
  ]);

  if (isLoading) {
    return <Loader />;
  }

  if (!isAuthenticated || isAccessDenied) {
    return null;
  }

  return <>{children}</>;
}
